import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from "@material-ui/core/styles";
import {
  Tab,
  Tabs,
  Typography,
  Selector,
  ISelectorItem,
} from "@thingsw/pitta-design-system";
import { useTranslation } from "react-i18next";

import { PlaybackPanel } from "../components/cameras/PlaybackPanel";
import { useDispatch, useSelector } from "react-redux";
import {
  CAMERA,
  loadCamera,
  loadCameras,
  loadFirmwareConfig,
  loadFirmwareSettings,
} from "../features/Camera/slice";
import { RootState } from "../features/store";
import clsx from "clsx";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { CameraSettingPanel } from "../components/cameras/CameraSettingPanel";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { ReportPanel } from "../components/cameras/ReportPanel";
import { GPSTrackingPanel } from "../components/gps-tracking/GPSTrackingPanel";

import { FileUploadModal } from "../components/FileUploadModal";
import { ScreenDefaultProps } from "../hoc/withViewerTemplate";
import { loadVODUploadList, VOD } from "../features/VOD/slice";
import _ from "lodash";
import { USER } from "../features/User/slice";
import { openToast } from "../features/Toast/slice";
import { AutoSizer } from "react-virtualized";
import Scrollbars from "react-custom-scrollbars";
import { THEME } from "../features/Theme/slice";
import { FileDownloadModal } from "../components/modals/FileDownloadModal";
import { CameraMenu } from "../components/uxui/CameraMenu";
import { MobileCameraMenu } from "../components/uxui/MobileCameraMenu";
import {
  ICameraInfo,
  Webviewer,
  SemanticDarkColors,
  UPLOADING_QUEUE_UPDATE_INTERVAL,
  PSN650,
  AvailableFw,
  PermissionProps,
} from "@thingsw/pitta-modules";
import { LiveViewPanelWrapper } from "../components/cameras/LiveViewPanelWrapper";
import { LIVEVIEW } from "../features/LiveView/slice";
import { setCameraInfo } from "../features/GPS/slice";
import CloudStorageTourModal from "../components/modals/CloudStorageTourModal";
import { getCloudStorageUsage } from "../apis/CloudStorage";

export type ScreenMode =
  | "playback"
  | "live-view"
  | "notifications"
  | "camera-cloud-storage"
  | "report"
  | "gps-tracking"
  | "settings"
  | "cloud-storage";

export interface CameraPlaybackScreenProps {
  mode: ScreenMode;
  psn: string;
  onRename?: (camera: ICameraInfo) => void;
  onDelete?: (camera: ICameraInfo) => void;
  onUpdate?: (camera: ICameraInfo) => void;
  onRebootCamera?: (camera: ICameraInfo) => void;
}

export const ScreenModeToIndex: { [key in ScreenMode]: number } = {
  playback: 0,
  "live-view": 1,
  notifications: 2,
  "camera-cloud-storage": 3,
  report: 4,
  "gps-tracking": 5,
  settings: 6,
  "cloud-storage": 7,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      height: "calc(var(--vh, 1vh) * 100 - 56px - 165px)",
      // mantis :  8831 - KimSeongHyun
      // Scroll Top을 위해 margin을 padding으로 수정
      paddingTop: (props: ScreenDefaultProps) =>
        48 + (props.headerDivRef?.offsetHeight ?? 56),
      // marginTop: (props: ScreenDefaultProps) =>
      //   48 + (props.headerDivRef?.offsetHeight ?? 56),
      [theme.breakpoints.up(Webviewer.mobile)]: {
        height: (props: ScreenDefaultProps) =>
          props.error
            ? "calc(var(--vh, 1vh) * 100 - 85px - 52px)"
            : "calc(var(--vh, 1vh) * 100 - 56px - 52px)",
        // margin: theme.spacing(5, 4, 2, 4),
        // mantis :  8831 - KimSeongHyun
        // Scroll Top을 위해 margin을 padding으로 수정
        paddingTop: (props: ScreenDefaultProps) =>
          props.error ? 48 + 85 : 48 + 56,
        // marginTop: (props: ScreenDefaultProps) =>
        //   props.error ? 48 + 85 : 48 + 56,
      },
    },
    tabDiv: {
      backgroundColor: (props: ScreenDefaultProps) =>
        props.color === "dark"
          ? SemanticDarkColors.primary["1"]
          : props.colors.primary["0"],
      top: (props: ScreenDefaultProps) =>
        props.headerDivRef?.offsetHeight ?? 56,
      ...(theme.direction === "rtl"
        ? { left: 0, right: 0 }
        : { left: 235, right: 0 }),
      position: "fixed",
      zIndex: 90,
      height: 53 + 29,
      display: "flex",
      alignItems: "flex-end",
      [theme.breakpoints.up(Webviewer.mobile)]: {
        padding: theme.spacing(0, 4),
        height: 53,
        ...(theme.direction === "rtl"
          ? { left: 0, right: 235 }
          : { left: 235, right: 0 }),
      },
    },
    //@ts-ignore
    tabScroll: {
      borderBottom: (props: ScreenDefaultProps) =>
        `1px solid ${props.colors.primary["5"]}`,
      overflowX: "scroll",
      "&::-webkit-scrollbar": {
        display: "none",
      },
      [theme.breakpoints.up(Webviewer.mobile)]: {
        overflowX: "unset",
        width: "100%",
      },
    },
    tab: {
      display: "flex",
      // justifyContent: "space-between",
      minWidth: 570,
      width: "100%",
      height: 52,
      alignItems: "flex-end",
    },
    tabDivClosed: {
      left: 73,
    },
    tabDivMobile: {
      left: 0,
      flexDirection: "column",
      alignItems: "unset",
    },
    tabItem: {
      display: "flex",
      alignItems: "flex-end",
      paddingBottom: 6,
      whiteSpace: "nowrap",
      color: (props: ScreenDefaultProps) => props.colors.primary["1"],
    },
    mobileCamNameDiv: {
      display: "flex",
      paddingTop: theme.spacing(1),
      justifyContent: "center",
    },
    downUploadModal: {
      position: "fixed",
      right: 0,
      bottom: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "end",
      zIndex: 99,
      [theme.breakpoints.up(Webviewer.mobile)]:
        theme.direction === "rtl"
          ? {
              width: 437,
              left: 32,
            }
          : {
              width: 437,
              right: 32,
            },
    },
    selectRoot: {
      maxWidth: 343,
      marginLeft: 32,
      "& .MuiPaper-root": {
        border: "none",
      },
    },
    selectPaper: {
      width: 343,
      padding: 8,
    },
  })
);

const initCamItem = {
  key: "",
  value: "",
};

export const CLOUD_ONBOARDING_TOUR = "pitta-cloud-storage-tour";

export const CameraPlaybackScreen = (
  props: CameraPlaybackScreenProps & PermissionProps & ScreenDefaultProps
) => {
  const {
    mode,
    psn,
    openMenu,
    onRename,
    onDelete,
    onUpdate,
    onRebootCamera,
    ...permissions
  } = props;

  const {
    gpsTrackingPerm,
    reportPerm,
    settingsCamPerm,
    renameCamPerm,
    fotaCamPerm,
    deleteCamPerm,
    liveviewPerm,
    rebootCamPerm,
  } = permissions;

  const classes = useStyles(props);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();
  const mobileMedia = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  const { fullscreen } = useSelector((state: RootState) => state[THEME]);
  const [mobile, setMobile] = useState(mobileMedia);

  useEffect(() => {
    if (!fullscreen) {
      setMobile(mobileMedia);
    }
  }, [fullscreen, mobileMedia]);

  const match = useRouteMatch<{ psn: string }>("/cameras/:psn");

  const cameraState = useSelector((state: RootState) => state[CAMERA]);
  const { cameraList, firmware } = cameraState;

  const userState = useSelector((state: RootState) => state[USER]);
  const { email } = userState;

  const { progressDisplayOrder } = useSelector(
    (state: RootState) => state[VOD]
  );
  const { multiliveview } = useSelector((state: RootState) => state[LIVEVIEW]);

  const [value, setValue] = useState(ScreenModeToIndex[mode]);

  const [openCamMenu, setOpenCamMenu] = useState(false);
  const [currentCam, setCurrentCam] = useState<ICameraInfo>();
  const [camList, setCamList] = useState<ISelectorItem[]>([initCamItem]);

  const anchorRef = useRef<HTMLDivElement>(null);

  const [backScroll /*, setBackScroll*/] = React.useState(false);
  const [openTourModal, setOpenTourModal] = React.useState(false);

  useEffect(() => {
    if (mode !== "cloud-storage") return;

    const tourString = localStorage.getItem(CLOUD_ONBOARDING_TOUR);
    const tour = JSON.parse(tourString ?? "[]");

    if (!_.includes(tour, email)) {
      setOpenTourModal(true);
    }
  }, [email, mode]);

  useEffect(() => {
    const body = document.getElementById("body-container");
    body?.setAttribute("style", "");
  });

  // useEffect(() => {
  //   if (!camera && cameraList) {
  //     dispatch(loadCamera(psn));
  //   }
  // }, [camera, cameraList, dispatch, psn]);

  useEffect(() => {
    const cam = _.chain(cameraList?.deviceListInfo)
      .map((c) => c.device)
      .find((c) => c.psn === psn)
      .value();

    setCurrentCam((c) => {
      if (c?.psn !== cam?.psn) {
        return cam;
      } else if (c?.active !== cam?.active) {
        return cam;
        // mantis - 11504, cam rename하면 모바일 상단 카메라명 변경되도록 수정 (Leehj)
      } else if (c?.dev_name !== cam?.dev_name) {
        return cam;
      }
      return c;
    });
    dispatch(setCameraInfo(cam));
  }, [psn, cameraList?.deviceListInfo, dispatch]);

  useEffect(() => {
    if (currentCam) {
      dispatch(loadFirmwareConfig(currentCam));
    }
  }, [currentCam, dispatch]);

  useEffect(() => {
    if (currentCam && firmware?.psn !== currentCam.psn) {
      const load = () => {
        dispatch(loadFirmwareSettings(currentCam.psn));
      };

      const timer = setTimeout(load, 500);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [currentCam, dispatch, firmware?.psn]);

  useEffect(() => {
    function tick() {
      if (currentCam) {
        dispatch(loadVODUploadList(currentCam));
      }
    }
    if (currentCam && currentCam.active === "on") {
      let timer: NodeJS.Timeout | null = null;
      let id = setInterval(tick, UPLOADING_QUEUE_UPDATE_INTERVAL);
      if (location.pathname.indexOf("live-view") > -1) {
        timer = setTimeout(tick, 500);
      } else {
        tick();
      }
      return () => {
        clearInterval(id);
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, [dispatch, currentCam, location]);

  useEffect(() => {
    if (currentCam?.psn !== psn) {
      if (psn === "none" && currentCam?.psn) {
        dispatch(loadCamera(currentCam?.psn));
      } else {
        dispatch(loadCamera(psn));
      }
    }
  }, [currentCam?.psn, dispatch, psn]);

  useEffect(() => {
    if (!cameraList) {
      dispatch(loadCameras());
    }
  }, [cameraList, dispatch]);

  useEffect(() => {
    setValue(ScreenModeToIndex[mode]);
  }, [mode]);

  // mantis : 8831 - KimSeongHyun
  // 탭 이동시 스크롤 최상위로 이동
  const scrollToTop = useCallback(() => {
    document.getElementById("scrollTop")?.scrollIntoView(true);
  }, []);

  const handleRename = useMemo(() => {
    return renameCamPerm
      ? () => {
          setOpenCamMenu(false);
          currentCam && onRename?.(currentCam);
          // setCurrentCam(camera?.dev_name);
        }
      : null;
  }, [currentCam, onRename, renameCamPerm]);

  const handleRebbotCam = useMemo(() => {
    return rebootCamPerm
      ? () => {
          currentCam && onRebootCamera?.(currentCam);
          setOpenCamMenu(false);
        }
      : null;
  }, [currentCam, onRebootCamera, rebootCamPerm]);

  const handleFOTA: (() => void) | null = useMemo(() => {
    return fotaCamPerm && !_.includes(PSN650, currentCam?.psn.substr(0, 4))
      ? (fw?: AvailableFw) => {
          if (fw) {
            if (currentCam && currentCam.active === "on") {
              if (fw.dmc200) {
                history.push(`/cameras/${currentCam.psn}/fota-dmc200`);
              } else if (fw.blackvue) {
                history.push(`/cameras/${currentCam.psn}/fota`);
              }
            }
          } else {
            dispatch(openToast({ message: "Firmware is up_" }));
            setOpenCamMenu(false);
          }
        }
      : null;
  }, [currentCam, dispatch, fotaCamPerm, history]);

  const handleDelete = useMemo(() => {
    return deleteCamPerm
      ? () => {
          setOpenCamMenu(false);
          currentCam && onDelete?.(currentCam);
        }
      : null;
  }, [currentCam, deleteCamPerm, onDelete]);

  const handleChangeCurrentCam = (cam: ICameraInfo) => {
    setCurrentCam(cam);
  };

  useEffect(() => {
    const settingCamList = async (email: string) => {
      if (cameraList?.deviceListInfo) {
        const usageList = await getCloudStorageUsage(email);

        const camList = cameraList?.deviceListInfo.map((c) => {
          const usageData = usageList.find((u) => u.psn === c.device.psn);

          if (!usageData) {
            return {
              key: c.device.psn,
              value: c.device.dev_name,
            };
          }

          return {
            key: c.device.psn,
            value:
              c.device.dev_name +
              "\n" +
              `Cloud Storage: ${usageData.usage}/${usageData.limit}GB`,
          };
        });
        setCamList([initCamItem, ...camList]);
      }
    };

    if (email) {
      settingCamList(email);
    }
  }, [cameraList, email]);

  const bodyMarkup = useMemo(() => {
    return (
      <>
        {mode === "cloud-storage" && (
          <div className={classes.selectRoot}>
            <Selector
              onType={(string) => {
                const newCamList = _.filter(cameraList?.deviceListInfo, (c) =>
                  c.device.dev_name.toLowerCase().includes(string.toLowerCase())
                ).map((c) => {
                  return {
                    key: c.device.psn,
                    value: c.device.dev_name,
                  };
                });
                setCamList([initCamItem, ...newCamList]);
              }}
              onChange={(item) => {
                const cam = _.find(cameraList?.deviceListInfo, (c) => {
                  return c.device.psn === item.key;
                })?.device;
                if (cam) {
                  handleChangeCurrentCam(cam);
                }
              }}
              dense
              t={t}
              select={false}
              menuItemClassName={classes.selectPaper}
              placeholder={t("Select or search the camera")}
              items={camList}
            />
          </div>
        )}
        {value === 0 && <PlaybackPanel openMenu={openMenu} {...permissions} />}
        {value === 1 && currentCam && (
          <LiveViewPanelWrapper camera={currentCam} {...permissions} />
        )}
        {value === 3 && (
          <PlaybackPanel
            openMenu={openMenu}
            {...permissions}
            mode="camera-cloud-storage"
          />
        )}
        {value === 5 && gpsTrackingPerm && (
          <GPSTrackingPanel
            plan={props.plan}
            openMenu={props.openMenu}
            {...permissions}
          />
        )}
        {value === 4 && reportPerm && (
          <ReportPanel openMenu={openMenu} {...permissions} />
        )}
        {value === 6 && settingsCamPerm && (
          <CameraSettingPanel {...permissions} />
        )}
        {value === 7 && (
          <PlaybackPanel
            openMenu={openMenu}
            {...permissions}
            mode="cloud-storage"
          />
        )}
      </>
    );
  }, [
    camList,
    cameraList?.deviceListInfo,
    classes.selectPaper,
    classes.selectRoot,
    currentCam,
    gpsTrackingPerm,
    mode,
    openMenu,
    permissions,
    props.openMenu,
    props.plan,
    reportPerm,
    settingsCamPerm,
    t,
    value,
  ]);

  return (
    // mantis :  8831 - KimSeongHyun
    // 탭 이동시 스크롤 최상위로 이동
    <div className={classes.root} dir={theme.direction} id="scrollTop">
      {mode !== "cloud-storage" && (
        <div
          className={clsx(classes.tabDiv, {
            [classes.tabDivClosed]: !openMenu,
            [classes.tabDivMobile]: mobile,
          })}
        >
          {mobile && (
            <div className={classes.mobileCamNameDiv}>
              <Typography
                category="Default"
                variant="SmallBold"
                htmlColor={props.colors.primary["2"]}
              >
                {currentCam?.dev_name}
              </Typography>
            </div>
          )}

          <div className={classes.tabScroll}>
            <div className={clsx(classes.tab)}>
              <Tabs value={value}>
                <Tab
                  className={classes.tabItem}
                  label={t("Playback")}
                  disabled={currentCam?.active === "off"}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/playback`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                  className={classes.tabItem}
                  label={t("Live view")}
                  disabled={
                    (currentCam?.active === "off" && !multiliveview) ||
                    !liveviewPerm
                  }
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/live-view`, {
                      clicked: true,
                    });
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                  className={classes.tabItem}
                  label={t("Notifications")}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/playback`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                  className={classes.tabItem}
                  label={t("Cloud Storage")}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/cloud-storage`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />
                <Tab
                  className={classes.tabItem}
                  disabled={
                    !reportPerm ||
                    _.includes(PSN650, currentCam?.psn.substr(0, 4))
                  }
                  label={t("Report")}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/report`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                  className={classes.tabItem}
                  disabled={!gpsTrackingPerm}
                  label={t("GPS tracking")}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/gps-tracking`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                  className={classes.tabItem}
                  disabled={!settingsCamPerm}
                  label={t("Settings")}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/settings`);
                    scrollToTop();
                  }}
                />
              </Tabs>

              <Tab
                ref={anchorRef}
                className={classes.tabItem}
                label={t("More")}
                onClick={() => setOpenCamMenu(true)}
              />
            </div>
          </div>
        </div>
      )}

      <div
        style={
          // 모바일에서 이벤트공유 모달 open시 background가 스크롤 되는현상 방지(HJ)
          mobile && backScroll
            ? { height: "100%", paddingTop: 30, overflow: "hidden" }
            : mobile
            ? { height: "100%", paddingTop: 30, overflow: "auto" }
            : { height: "100%" }
        }
      >
        {mobile ? (
          bodyMarkup
        ) : (
          <AutoSizer>
            {({ width, height }) => (
              <Scrollbars style={{ height, width }} autoHide>
                {bodyMarkup}
              </Scrollbars>
            )}
          </AutoSizer>
        )}
      </div>
      {!mobile && (
        // mantis - 11504, uxui CameraMenu로 컴포넌트 변경 (Leehj)
        <CameraMenu
          {...props}
          open={openCamMenu}
          camera={currentCam}
          anchorRef={anchorRef}
          placement="bottom-end"
          onClickAway={() => setOpenCamMenu(false)}
          onEditProfile={handleRename}
          onUpdate={handleFOTA}
          onDelete={handleDelete}
          onRebootCamera={handleRebbotCam}
        />
      )}
      {mobile && (
        // mantis - 11504, uxui CameraMenu로 컴포넌트 변경 (Leehj)
        <MobileCameraMenu
          {...props}
          camera={currentCam}
          open={openCamMenu}
          onClose={() => setOpenCamMenu(false)}
          onEditProfile={handleRename}
          onUpdate={handleFOTA}
          onDelete={handleDelete}
          onRebootCamera={handleRebbotCam}
        />
      )}
      <CloudStorageTourModal
        openTourModal={openTourModal}
        setOpenTourModal={setOpenTourModal}
      />
      <div
        className={classes.downUploadModal}
        style={{ flexDirection: progressDisplayOrder }}
      >
        <FileUploadModal />
        {/* mantis - 12598, 다운로드 취소하는 경우 진행중인 업로드 취소하기위해 psn 필요 (Leehj) */}
        <FileDownloadModal camera={currentCam} />
      </div>
    </div>
  );
};
